'use client';

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Page } from '@/types/page';
import renderBlock from './renderBlock';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import SidebarLayout from '@/components/layouts/SidebarLayout';

type Props = {
  page: Page;
  heading: string;
};

const PageClientComponent: React.FC<Props> = ({ page, heading }) => {
  useEffect(() => {
    if (!page || !page.page_color) return;

    const body = document.body;
    const newClass = `bg-${page.page_color}`;

    body.classList.forEach((cls) => {
      if (cls.startsWith('bg-')) {
        body.classList.remove(cls);
      }
    });

    // Add the new class
    body.classList.add(newClass);

    return () => {
      body.classList.remove(newClass);
    };
  }, [page?.slug, page?.page_color, page]);

  const blocks = page.blocks || [];

  const renderContent = (
    <AnimatePresence mode="wait">
      <motion.div
        key={page.slug}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {blocks.map((block, index) => {
          if (!block) return null;

          const id =
            'heading' in block && typeof block.heading === 'string'
              ? block.heading.toLowerCase().replace(/\s+/g, '-')
              : `section-${index}`;

          return (
            <section id={id} key={block.__typename + index} className="scroll-mt-24">
              {renderBlock(block)}
            </section>
          );
        })}
      </motion.div>
    </AnimatePresence>
  );

  if (page.template_type === 'sidebar') {
    return (
      <SidebarLayout page={page} heading={heading}>
        {renderContent}
      </SidebarLayout>
    );
  }

  return <DefaultLayout page={page}>{renderContent}</DefaultLayout>;
};

export default PageClientComponent;
